import { LitElement, html } from 'lit';
import { formatCurrency } from '../../utils/format-utils.js';
import './quote-line-item.js';
import './letterbox-distribution-link.js';
import '../icons/expand-icon.js';
import '../icons/collapse-icon.js';

/**
 * Quote Class Section Component
 * Displays a service class (e.g., Letterbox, Digital) with its billing lines
 * Shows summary totals for the class
 *
 * Modes:
 * - Full (default): Shows all line items with column headers and details
 * - Compact: Shows only class name and subtotal in a single row
 *
 * To enable compact mode, pass the `compact` attribute from parent:
 * <quote-class-section .classData="${data}" ?compact="${true}"></quote-class-section>
 *
 * Note: Subtotal is currently calculated client-side from line totals.
 * Future enhancement: Accept pre-calculated subtotal from backend API via classData.subtotal field.
 */
class QuoteClassSection extends LitElement {
    static properties = {
        classData: { type: Object },
        sputnikQuoteId: { type: String },
        compact: { type: Boolean },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.classData = null;
        this.sputnikQuoteId = null;
        this.compact = true;
    }

    get className() {
        return this.classData?.classDesc || 'Service';
    }

    get lines() {
        return this.classData?.lines || [];
    }

    /**
     * Parse a currency string (e.g., "$512.55") to a numeric value
     * @param {string} totalString - Currency string with optional $ and commas
     * @returns {number} - Numeric value
     */
    parseTotal(totalString) {
        if (!totalString) return 0;
        // Remove $, commas, and whitespace, then parse
        const cleaned = totalString.replace(/[$,\s]/g, '');
        const value = parseFloat(cleaned);
        return isNaN(value) ? 0 : value;
    }

    /**
     * Get the subtotal for this class
     * Uses API-provided classTotal, falls back to calculating from line totals
     */
    get classSubtotal() {
        if (this.classData?.classTotal != null) {
            return formatCurrency(this.classData.classTotal);
        }

        // Fallback: calculate from line totals
        const sum = this.lines.reduce((acc, line) => {
            return acc + this.parseTotal(line.total || '$0.00');
        }, 0);

        return formatCurrency(sum);
    }

    renderColumnHeaders() {
        return html`
            <div
                class="hidden md:grid md:grid-cols-12 gap-4 px-3 py-2 bg-base-200 border-b border-base-300"
            >
                <div class="col-span-5">
                    <span class="field-label-inline">Description</span>
                </div>
                <div class="col-span-2 text-right">
                    <span class="field-label-inline">Quantity</span>
                </div>
                <div class="col-span-3 text-right">
                    <span class="field-label-inline">Rate</span>
                </div>
                <div class="col-span-2 text-right">
                    <span class="field-label-inline">Total</span>
                </div>
            </div>
        `;
    }

    /**
     * Toggle between compact and full view
     */
    _toggleView() {
        this.compact = !this.compact;
    }

    /**
     * Render expand/collapse toggle button
     */
    renderToggleButton() {
        return html`
            <button
                class="p-1.5 rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                @click="${this._toggleView}"
                title="${this.compact ? 'Expand details' : 'Collapse details'}"
                aria-label="${this.compact ? 'Expand details' : 'Collapse details'}"
                aria-expanded="${!this.compact}"
            >
                ${this.compact
                    ? html`<expand-icon size="4" class="block text-gray-600"></expand-icon>`
                    : html`<collapse-icon size="4" class="block text-gray-600"></collapse-icon>`}
            </button>
        `;
    }

    /**
     * Render section header with toggle
     */
    renderHeader() {
        return html`
            <div class="bg-base-200 px-4 py-3 border-b border-base-300">
                <div class="flex flex-row items-center justify-between gap-3">
                    <h3 class="text-lg font-bold text-gray-900">${this.className}</h3>

                    <div class="flex items-center gap-3">
                        ${this.className.toLowerCase().startsWith('letterbox')
                            ? html`
                                  <letterbox-distribution-link
                                      .sputnikQuoteId="${this.sputnikQuoteId}"
                                  ></letterbox-distribution-link>
                              `
                            : ''}
                        ${this.renderToggleButton()}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render content based on compact state
     */
    renderContent() {
        if (this.compact) {
            return html`
                <!-- Mailers Volume Row (Letterbox only) -->
                ${this.className.toLowerCase().startsWith('letterbox') &&
                this.classData?.mailersVolume
                    ? html`
                          <div
                              class="px-4 py-3 border-b border-base-300"
                              role="row"
                              aria-label="Mailers Volume ${this.classData.mailersVolume}"
                          >
                              <div
                                  class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                              >
                                  <span class="text-base text-gray-700 font-medium"
                                      >Mailers Volume</span
                                  >
                                  <span class="text-lg font-bold text-gray-900"
                                      >${this.classData.mailersVolume}</span
                                  >
                              </div>
                          </div>
                      `
                    : ''}

                <!-- Compact Subtotal Row -->
                <div
                    class="px-4 py-3"
                    role="row"
                    aria-label="${this.className} subtotal ${this.classSubtotal}"
                >
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <span class="text-base text-gray-700 font-medium">Subtotal</span>
                        <span class="text-lg font-bold text-gray-900">${this.classSubtotal}</span>
                    </div>
                </div>
            `;
        }

        return html`
            <!-- Column Headers (Desktop only) -->
            ${this.renderColumnHeaders()}

            <!-- Line Items -->
            <div class="divide-y divide-base-300">
                ${this.lines.map(
                    (line) => html` <quote-line-item .line="${line}"></quote-line-item> `
                )}
            </div>
        `;
    }

    render() {
        if (!this.classData || !this.lines.length) {
            return html``;
        }

        return html`
            <div class="card-standard mb-8 overflow-hidden">
                <div class="card-body p-0">${this.renderHeader()} ${this.renderContent()}</div>
            </div>
        `;
    }
}

customElements.define('quote-class-section', QuoteClassSection);
